/* Live cart estimate follows the installed Clean Sweeps cart widget rules.
 * UpCart public events and native theme success events trigger fresh cart reads.
 * No cart mutation or checkout accounting is performed by this component.
 */
(() => {
  if (customElements.get('sac-cart-entries')) return;
  const endpoint = 'https://backend.cleansweeps.app/graphql';
  const query = `query SacCityCartEntries($platform: String!, $platformId: String!, $productIds: [String!]) {
    organization(platform: $platform, platformId: $platformId) {
      discountCodes
      sweepstakes { id entryManagement bonusSpendingEnabledProductOnly isActive }
    }
    sweepstakeDetailsByProductId(platform: $platform, platformId: $platformId, productIds: $productIds) {
      id entryManagement productsAllowedByDefault bonusEntriesDefault
      membershipManagement membershipProductMultOverride calculateWithDiscount
      scheduledBonusValue scheduledBonusActive
      products(productIds: $productIds) {
        productId productAllowed productBonusEntries productMultiplier
        orderMultiplierAllowed orderMultiplier
      }
    }
  }`;
  const referralQuery = `query SacCityCartReferral($referralCode: String, $platform: String, $platformId: String) {
    validateReferralCode(referralCode: $referralCode, platform: $platform, platformId: $platformId) {
      isDeleted referralBonusGive
    }
  }`;
  const normalizeId = value => String(value).replace(/\D/g, '');

  async function request(queryText, variables) {
    const response = await fetch(endpoint, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      signal: AbortSignal.timeout(10000),
      body: JSON.stringify({ query: queryText, variables })
    });
    if (!response.ok) throw new Error('Entry settings unavailable');
    const result = await response.json();
    if (!result.data || result.errors?.length) throw new Error('Entry settings unavailable');
    return result.data;
  }

  class SacCartEntries extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.sequence = (this.sequence || 0) + 1;
      this.widgets = new Set();
      this.cache = new Map();
      this.ownedBubbleNodes = new WeakSet();
      this.cleanups = [];
      this.format = new Intl.NumberFormat(document.documentElement.lang || 'en');
      this.platformId = `gid://shopify/Shop/${this.dataset.shopId}`;
      try { this.customerTags = JSON.parse(this.querySelector('[data-customer-tags]').textContent); }
      catch { this.customerTags = []; }
      const listen = (target, event, callback) => {
        target.addEventListener(event, callback);
        this.cleanups.push(() => target.removeEventListener(event, callback));
      };
      this.header = document.querySelector('[data-header-entries]');
      this.lastHeaderSignature = null;
      if (this.header) {
        const close = (restoreFocus = false) => {
          this.header.open = false;
          if (restoreFocus) this.header.querySelector('[data-header-entry-toggle]').focus();
        };
        listen(this.header.querySelector('[data-header-entry-close]'), 'click', () => close(true));
        listen(this.header, 'keydown', event => {
          if (event.key === 'Escape' && this.header.open) {
            event.preventDefault();
            close(true);
          }
        });
        listen(this.header.querySelector('[data-header-entry-retry]'), 'click', () => {
          this.cache.clear();
          this.schedule();
        });
        const cartLink = document.getElementById('cart-icon');
        if (cartLink) listen(cartLink, 'click', () => close());
        for (const disclosure of document.querySelectorAll('.sac-header__tools details')) {
          if (disclosure !== this.header) listen(disclosure, 'toggle', () => {
            if (disclosure.open) close();
          });
        }
      }
      const changed = event => {
        if (event?.detail?.cart?.items?.length === 0) this.paint({ empty: true });
        this.mount();
        this.schedule();
      };
      for (const event of ['cart-loaded', 'cart-updated', 'cart-opened']) {
        listen(window, `aftersell-upcart:public-events:${event}`, changed);
      }
      for (const event of ['on:cart:add', 'on:line-item:change', 'on:cart-drawer:before-open', 'on:bfcache:load-restore']) {
        listen(document, event, changed);
      }
      listen(window, 'pageshow', changed);
      listen(document, 'visibilitychange', () => { if (!document.hidden) changed(); });
      const bubble = document.getElementById('cart-icon')?.querySelector('#cart-icon-bubble');
      if (bubble) {
        this.bubbleObserver = new MutationObserver(records => {
          if (records.length && records.every(record => record.type === 'childList'
            && record.target === bubble && record.addedNodes.length === 1
            && this.ownedBubbleNodes.has(record.addedNodes[0]))) return;
          this.mount();
          this.schedule();
        });
        this.bubbleObserver.observe(bubble, { childList: true, characterData: true, subtree: true });
      }
      this.mount();
      this.schedule();
    }

    disconnectedCallback() {
      this.connected = false;
      this.sequence++;
      clearTimeout(this.timer);
      this.abort?.abort();
      this.cleanups?.forEach(cleanup => cleanup());
      this.bubbleObserver?.disconnect();
      this.drawerObserver?.disconnect();
      this.observedBody = null;
      this.widgets?.forEach(widget => widget.remove());
      if (this.header) { this.header.open = false; this.header.hidden = true; }
    }

    mount() {
      if (!this.connected) return;
      const root = window.upcartDocumentOrShadowRoot || document.querySelector('#upCart')?.shadowRoot;
      const upcartBody = root?.querySelector('.upcart-cart-body');
      const nativeBody = document.querySelector('cart-drawer .cart-drawer__summary');
      for (const target of [upcartBody, nativeBody].filter(Boolean)) {
        if (target.querySelector('[data-sac-cart-entry-widget]')) continue;
        const fragment = this.querySelector('[data-entry-template]').content.cloneNode(true);
        const widget = fragment.querySelector('.sac-cart-entries');
        widget.dataset.sacCartEntryWidget = '';
        widget.querySelector('[data-entry-retry]').addEventListener('click', () => {
          this.cache.clear();
          this.schedule();
        });
        target.prepend(fragment);
        this.widgets.add(widget);
        this.paint(this.state);
      }
      for (const widget of this.widgets) if (!widget.isConnected) this.widgets.delete(widget);
      if (upcartBody && this.observedBody !== upcartBody) {
        this.drawerObserver?.disconnect();
        this.observedBody = upcartBody;
        // Only re-mount a removed summary. Our own text updates never fetch the cart.
        this.drawerObserver = new MutationObserver(() => {
          if (!upcartBody.querySelector('[data-sac-cart-entry-widget]')) this.mount();
        });
        this.drawerObserver.observe(upcartBody, { childList: true });
      }
    }

    schedule() {
      if (!this.connected) return;
      this.sequence++;
      this.abort?.abort();
      clearTimeout(this.timer);
      if (this.state && !this.state.empty) this.paint({ pending: true });
      const sequence = this.sequence;
      this.timer = setTimeout(() => this.refresh(sequence), 100);
    }

    async settings(ids) {
      const key = [...ids].sort().join(',');
      const cached = this.cache.get(key);
      if (cached && Date.now() - cached.time < 60000) return cached.promise;
      const promise = (async () => {
        let organization;
        let detail;
        for (let offset = 0; offset < ids.length; offset += 50) {
          const data = await request(query, { platform: 'Shopify', platformId: this.platformId, productIds: ids.slice(offset, offset + 50) });
          const active = data.organization?.sweepstakes?.find(sweepstake => normalizeId(sweepstake.id) === this.dataset.sweepstakeId && sweepstake.isActive === true);
          const batchDetail = data.sweepstakeDetailsByProductId?.find(sweepstake => normalizeId(sweepstake.id) === this.dataset.sweepstakeId);
          if (!active || !batchDetail || !Array.isArray(batchDetail.products)) throw new Error('Active giveaway unavailable');
          organization = data.organization;
          if (detail) detail.products.push(...batchDetail.products);
          else detail = { ...batchDetail, products: [...batchDetail.products] };
        }
        return { organization, detail };
      })();
      this.cache.set(key, { time: Date.now(), promise });
      try { return await promise; }
      catch (error) { this.cache.delete(key); throw error; }
    }

    updateCartCount(cart) {
      if (!Number.isSafeInteger(cart.item_count) || cart.item_count < 0) return;
      let quantity = 0;
      for (const item of cart.items) {
        if (!Number.isSafeInteger(item.quantity) || item.quantity < 1) return;
        quantity += item.quantity;
      }
      if (!Number.isSafeInteger(quantity) || quantity !== cart.item_count) return;
      const bubble = document.getElementById('cart-icon')?.querySelector('#cart-icon-bubble');
      const count = String(cart.item_count);
      if (!bubble || bubble.textContent === count) return;
      bubble.textContent = count;
      if (bubble.firstChild) this.ownedBubbleNodes.add(bubble.firstChild);
    }

    async refresh(sequence) {
      const current = () => this.connected && sequence === this.sequence;
      const controller = new AbortController();
      this.abort = controller;
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(this.dataset.cartUrl, { headers: { Accept: 'application/json' }, cache: 'no-store', signal: controller.signal });
        if (!response.ok) throw new Error('Cart unavailable');
        const cart = await response.json();
        if (!current()) return;
        if (!Array.isArray(cart.items)) throw new Error('Cart unavailable');
        this.updateCartCount(cart);
        if (!cart.items.length) { this.paint({ empty: true }); return; }
        this.paint({ pending: true });
        const ids = [...new Set(cart.items.map(item => `gid://shopify/Product/${item.product_id}`))];
        const settings = await this.settings(ids);
        if (!current()) return;
        let referralBonus = 0;
        if (cart.note?.includes('Referral Code:')) {
          const referralCode = cart.note.replace('Referral Code:', '').trim();
          const data = await request(referralQuery, { referralCode, platform: 'Shopify', platformId: this.platformId });
          if (!data.validateReferralCode) throw new Error('Referral unavailable');
          if (!data.validateReferralCode.isDeleted) referralBonus = data.validateReferralCode.referralBonusGive || 0;
        }
        if (!current()) return;
        const result = window.SacCartEntryRules.calculate({ cart, ...settings, customerTags: this.customerTags, referralBonus });
        if (!result) throw new Error('Entry estimate unavailable');
        const signature = JSON.stringify([
          cart.items.map(item => [item.key || item.variant_id || item.product_id, item.quantity, item.final_line_price, item.line_price]),
          cart.total_price, cart.note, result.total
        ]);
        this.paint({ result, signature });
      } catch (error) {
        if (current()) this.paint({ error: true });
      } finally { clearTimeout(timeout); }
    }

    paint(state) {
      this.state = state;
      if (this.header) {
        this.header.hidden = !state || !!state.empty;
        if (this.header.hidden) {
          this.header.open = false;
          this.lastHeaderSignature = null;
        } else {
          this.header.querySelector('[data-header-entry-total]').textContent = state.result
            ? this.dataset.compactCountLabel.replace('__COUNT__', this.format.format(state.result.total))
            : state.error ? this.dataset.errorLabel : this.dataset.loadingLabel;
          this.header.querySelector('[data-header-entry-retry]').hidden = !state.error;
          if (state.result && state.signature !== this.lastHeaderSignature) {
            const anotherDisclosureOpen = [...document.querySelectorAll('.sac-header__tools details')]
              .some(disclosure => disclosure !== this.header && disclosure.open);
            if (!anotherDisclosureOpen) this.header.open = true;
            this.lastHeaderSignature = state.signature;
          }
        }
      }
      for (const widget of this.widgets) {
        widget.hidden = !state || !!state.empty;
        if (widget.hidden) continue;
        const result = state.result;
        widget.querySelector('[data-entry-total]').textContent = result
          ? this.dataset.countLabel.replace('__COUNT__', this.format.format(result.total))
          : state.error ? this.dataset.errorLabel : this.dataset.loadingLabel;
        widget.querySelector('[data-entry-breakdown]').hidden = !result;
        widget.querySelector('[data-entry-retry]').hidden = !state.error;
        if (!result) continue;
        widget.querySelector('[data-entry-products]').textContent = this.format.format(result.productEntries);
        const extra = result.membershipBonus + result.discountEntries + result.referralBonus;
        const multiplier = result.orderMultiplier * result.discountMultiplier;
        for (const [name, value] of [['spending', result.spendingBonus], ['extra', extra], ['multiplier', multiplier]]) {
          widget.querySelector(`[data-entry-${name}-row]`).hidden = name === 'multiplier' ? value <= 1 : value <= 0;
          widget.querySelector(`[data-entry-${name}]`).textContent = `${this.format.format(value)}${name === 'multiplier' ? '×' : ''}`;
        }
      }
    }
  }
  customElements.define('sac-cart-entries', SacCartEntries);
})();
